import React, { useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useTranslation } from 'react-i18next'
import { isEmpty } from 'lodash'
import { useSetRecoilState } from 'recoil'
import { openModalState } from 'recoils/atoms'

import { Button, Modal, PromptGuard } from 'components'
import {
  initialFormRequestCloseTicket,
  schemaFormRequestCloseTicket,
  formatCloseTicket,
  ISchemaFormCloseTicketPayload,
} from 'features/Ticket'

import TicketRequestCloseModalForm from './TicketRequestCloseModalForm'

import type { ISchemaFormCloseTicket } from '../model'

import { usePromptForm } from 'helpers/hooks'

interface IRequestCloseModal {
  id: string
  isOpen: boolean
  onClose: () => void
  payloadForCloseTask: ISchemaFormCloseTicketPayload
  onRefreshDetail: () => void
  setConfirmModal: () => void
  setPayloadForCloseTask: (payload: ISchemaFormCloseTicketPayload) => void
  setOpenRequestCloseTicket: () => void
}

function TicketRequestCloseModal(props: IRequestCloseModal): JSX.Element {
  const { t } = useTranslation(['common', 'ticket'])
  const setIsOpenModal = useSetRecoilState(openModalState)

  const {
    isOpen,
    onClose,
    payloadForCloseTask,
    setPayloadForCloseTask,
    setConfirmModal,
    setOpenRequestCloseTicket,
  } = props
  const base = 'f-ticket-request-close-modal'

  const methods = useForm({
    defaultValues: initialFormRequestCloseTicket,
    resolver: yupResolver(schemaFormRequestCloseTicket),
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  })

  const [shouldPrompt, setShouldPrompt] = usePromptForm(methods)

  useEffect(() => {
    if (shouldPrompt || methods.formState.isSubmitted) {
      return
    }

    if (methods.formState.isDirty) {
      setShouldPrompt(true)
    }
  }, [methods.formState])

  useEffect(() => {
    methods.setValue(
      'completedTaskIssue',
      payloadForCloseTask.completedTaskIssue,
      {
        shouldDirty: true,
      },
    )
    methods.setValue('completedDetail', payloadForCloseTask.completedDetail, {
      shouldDirty: true,
    })
  }, [])

  const beforeSubmit = async (value: ISchemaFormCloseTicket) => {
    const payload = formatCloseTicket(value)
    if (isEmpty(methods.errors) && payload) {
      setConfirmModal()
      setPayloadForCloseTask(payload)

      await setTimeout(() => {
        setShouldPrompt(false)
      }, 2000)
    }
  }

  const handleBackUpPayloadCloseTask = (key: string, value: string) => {
    setPayloadForCloseTask({
      ...payloadForCloseTask,
      [key]: value,
    })
  }

  const handleCloseModal = () => {
    setIsOpenModal(false)

    if (shouldPrompt) {
      onClose()
    } else {
      setOpenRequestCloseTicket()
      setPayloadForCloseTask(initialFormRequestCloseTicket)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCloseModal}
      isIcon={false}
      isFilledModal
    >
      <div className={`${base}__content`}>
        <div className={`${base}__modal-header`}>
          <span className={`${base}__modal-header--title`}>
            {t('ticket:requestCloseTicket')}
          </span>
        </div>

        <FormProvider {...methods}>
          <PromptGuard when={shouldPrompt} />

          <form
            className={`${base}__form`}
            onSubmit={methods.handleSubmit(beforeSubmit)}
          >
            <TicketRequestCloseModalForm
              backupPayloadCloseTask={handleBackUpPayloadCloseTask}
              payloadForCloseTask={payloadForCloseTask}
              setPayloadForCloseTask={setPayloadForCloseTask}
            />
            <div className={`${base}__button`}>
              <Button
                className={`${base}__button--action-button`}
                variants='secondary'
                onClick={handleCloseModal}
              >
                {t('common:cancel.button')}
              </Button>
              <Button
                className={`${base}__button--action-button`}
                type='submit'
              >
                {t('common:submit.button')}
              </Button>
            </div>
          </form>
        </FormProvider>
      </div>
    </Modal>
  )
}

export default TicketRequestCloseModal
